import {
  Assessment,
  AssignmentInd,
  CampaignOutlined,
  CompareArrowsOutlined,
  DashboardOutlined,
  DevicesOther,
  Extension,
  HeadsetMicOutlined,
  Inventory2Outlined,
  NotificationsOutlined,
  People,
  QueryStatsOutlined,
  RocketLaunch,
  SellOutlined,
  ShoppingCartOutlined,
  SpaceDashboard,
  TrendingUp,
  VideoLibrary,
} from "@mui/icons-material";
import { Box, Divider, Typography } from "@mui/material";
import { Menu, MenuProps, useCanAccessResources, useTranslate } from "react-admin";

import { hasStaffNavigationAccess, type PortalResource } from "../auth/access";
import { CrmObjectIcon } from "../components/crm/CrmObjectIcon";
import { CustomMenuItem } from "../components/CustomMenuItem";
import { Icon } from "../components/icons";
import { MenuItemWithCount } from "../components/MenuItemWithCount";
import {
  BILLWERK_CUSTOMER_MATCH_ROUTE,
  BILLWERK_PLAN_VARIANTS_ROUTE,
  FINANCE_NOTIFICATIONS_ROUTE,
  FINANCE_OVERVIEW_ROUTE,
  INTEGRATIONS_ROUTE,
  LOGISTICS_DHL_SETTINGS_ROUTE,
  LOGISTICS_INVENTORY_ROUTE,
  LOGISTICS_ROUTE,
  ResourceName,
  SALESFORCE_DASHBOARD_ROUTE,
  SALESFORCE_REPORTS_ROUTE,
  WEB_ORDERS_RESOURCE,
} from "../constants/resources";
import { CrmObject } from "../generated/graphql";
import { useCrmLabels } from "../hooks/useCrmLabels";
import { CRM_DUPLICATES_ROUTE, CRM_MENU_OBJECTS, CRM_ROUTE, crmListPath } from "../lib/crm";
import { MARKETING_ROUTE, WEB_ORDERS_ROUTE } from "../lib/marketing";
import { color, space, typography } from "../theme/designTokens";

type MenuItem = {
  resource: ResourceName;
  to: string;
  icon: React.ReactElement;
  showCount?: boolean;
};

type MenuSection = {
  titleKey?: string;
  items: MenuItem[];
};

const menuSections: MenuSection[] = [
  {
    titleKey: "learningGrants.section",
    items: [
      {
        resource: ResourceName.LearningAssignment,
        to: "/LearningAssignment",
        icon: <AssignmentInd sx={{ color: color.typographyBase }} />,
        showCount: false,
      },
      {
        resource: ResourceName.LearningProgress,
        to: "/LearningProgress",
        icon: <Assessment sx={{ color: color.typographyBase }} />,
        showCount: false,
      },
    ],
  },
  {
    titleKey: "facilities",
    items: [
      {
        resource: ResourceName.Organization,
        to: "/Organization",
        icon: <Icon type="home" size="large" />,
      },
    ],
  },
  {
    titleKey: "menu.dataView",
    items: [
      {
        resource: ResourceName.Practitioner,
        to: "/Practitioner",
        icon: <People sx={{ color: color.typographyBase }} />,
      },
      {
        resource: ResourceName.Patient,
        to: "/Patient",
        icon: <Icon type="patient" size="large" />,
      },
      {
        resource: ResourceName.Device,
        to: "/Device",
        icon: <Icon type="device" size="large" />,
      },
    ],
  },
];

const managerPrimaryItems: MenuItem[] = [
  {
    resource: ResourceName.Practitioner,
    to: "/Practitioner",
    icon: <People sx={{ color: color.typographyBase }} />,
  },
  {
    resource: ResourceName.Patient,
    to: "/Patient",
    icon: <Icon type="patient" size="large" />,
  },
  {
    resource: ResourceName.Device,
    to: "/Device",
    icon: <Icon type="device" size="large" />,
  },
];

const managerPrimaryResources = managerPrimaryItems.map(item => item.resource);

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Typography
    variant="body2"
    my={space.xxs}
    mx={space.xs}
    sx={{
      color: color.secondary40,
      lineHeight: typography.lineHeight.md,
      letterSpacing: typography.letterSpacing.sm,
    }}
  >
    {children}
  </Typography>
);

const MENU_RESOURCES = Array.from(
  new Set<PortalResource>([
    ResourceName.Location,
    ResourceName.Integrations,
    ResourceName.SalesforceDashboard,
    ResourceName.SalesforceReports,
    ResourceName.Marketing,
    WEB_ORDERS_RESOURCE,
    ResourceName.Crm,
    ResourceName.FinanceNotifications,
    ResourceName.BillwerkCustomerMatch,
    ResourceName.BillwerkPlanVariants,
    ResourceName.FinanceOverview,
    ResourceName.LogisticsDhlSettings,
    ResourceName.LogisticsInventory,
    ResourceName.Analytics,
    ...managerPrimaryResources,
    ...menuSections.flatMap(section => section.items.map(item => item.resource)),
  ])
);

export const CustomMenu = (props: MenuProps) => {
  const { canAccess } = useCanAccessResources({ action: "list", resources: MENU_RESOURCES });
  const can = (resource: PortalResource) => canAccess?.[resource] === true;
  const translate = useTranslate();
  const hasStaffAccess = hasStaffNavigationAccess(canAccess);
  const showManagerNavigation = can(ResourceName.Location) && !hasStaffAccess;
  const crmLabels = useCrmLabels();
  const webOrdersItem = (
    <CustomMenuItem
      to={WEB_ORDERS_ROUTE}
      primaryText={translate("marketing.webOrders.title")}
      leftIcon={<ShoppingCartOutlined sx={{ color: color.typographyBase }} />}
    />
  );

  return (
    <Menu {...props}>
      {(can(ResourceName.Crm) ||
        can(ResourceName.Marketing) ||
        can(ResourceName.FinanceOverview) ||
        can(ResourceName.SalesforceDashboard) ||
        can(ResourceName.SalesforceReports)) && (
        <Box data-testid="dashboards-menu">
          <SectionTitle>{translate("menu.dashboards")}</SectionTitle>
          {can(ResourceName.Crm) && (
            <CustomMenuItem
              to={CRM_ROUTE}
              exact
              primaryText={translate("menu.customerServiceDashboard")}
              leftIcon={<HeadsetMicOutlined sx={{ color: color.typographyBase }} />}
            />
          )}
          {can(ResourceName.Marketing) && (
            <CustomMenuItem
              to={MARKETING_ROUTE}
              exact
              primaryText={translate("marketing.title")}
              leftIcon={<CampaignOutlined sx={{ color: color.typographyBase }} />}
            />
          )}
          {can(ResourceName.FinanceOverview) && (
            <CustomMenuItem
              to={FINANCE_OVERVIEW_ROUTE}
              primaryText={translate("financeNotifications.section")}
              leftIcon={<DashboardOutlined sx={{ color: color.typographyBase }} />}
            />
          )}
          {can(ResourceName.SalesforceDashboard) && (
            <CustomMenuItem
              to={SALESFORCE_DASHBOARD_ROUTE}
              primaryText={translate("menu.subscriptionDashboard")}
              leftIcon={<Assessment sx={{ color: color.typographyBase }} />}
            />
          )}
          {can(ResourceName.SalesforceReports) && (
            <CustomMenuItem
              to={SALESFORCE_REPORTS_ROUTE}
              primaryText={translate("menu.salesforceReports")}
              leftIcon={<QueryStatsOutlined sx={{ color: color.typographyBase }} />}
            />
          )}
          <Divider sx={{ mx: space.xs, my: space.xs }} />
        </Box>
      )}
      {showManagerNavigation && (
        <Box>
          <CustomMenuItem
            to="/"
            primaryText={translate("start")}
            leftIcon={<SpaceDashboard sx={{ color: color.typographyBase }} />}
          />
          {managerPrimaryItems
            .filter(item => can(item.resource))
            .map(item => (
              <MenuItemWithCount key={item.resource} resource={item.resource} to={item.to} icon={item.icon} />
            ))}
          <Divider sx={{ mx: space.xs, my: space.xs }} />
        </Box>
      )}
      {can(ResourceName.Marketing) && (
        <Box data-testid="marketing-menu">
          <SectionTitle>{translate("marketing.title")}</SectionTitle>
          {webOrdersItem}
          {can(ResourceName.Crm) && (
            <CustomMenuItem
              to={crmListPath(CrmObject.Campaign)}
              primaryText={crmLabels.object(CrmObject.Campaign, true)}
              leftIcon={<CrmObjectIcon object={CrmObject.Campaign} size="small" />}
            />
          )}
          <Divider sx={{ mx: space.xs, my: space.xs }} />
        </Box>
      )}
      {/* Copies of Salesforce. */}
      {can(ResourceName.Crm) && (
        <Box data-testid="crm-menu">
          <SectionTitle>{translate("crm.title")}</SectionTitle>
          <CustomMenuItem
            to={CRM_DUPLICATES_ROUTE}
            primaryText={translate("crm.duplicates.title")}
            leftIcon={<CompareArrowsOutlined sx={{ color: color.typographyBase }} />}
          />
          {/* Staff without the Marketing menu still repair stuck web orders. */}
          {!can(ResourceName.Marketing) && can(WEB_ORDERS_RESOURCE) && webOrdersItem}
          {CRM_MENU_OBJECTS.map(object => (
            <CustomMenuItem
              key={object}
              to={crmListPath(object)}
              primaryText={crmLabels.object(object, true)}
              leftIcon={<CrmObjectIcon object={object} size="small" />}
            />
          ))}
          <Divider sx={{ mx: space.xs, my: space.xs }} />
        </Box>
      )}
      {/* The backend's own logistics records, read-only until the cutover. */}
      {(can(ResourceName.LogisticsInventory) || can(ResourceName.LogisticsDhlSettings)) && (
        <Box data-testid="logistics-menu">
          <SectionTitle>{translate("logistics.section")}</SectionTitle>
          {can(ResourceName.LogisticsInventory) && (
            <>
              <CustomMenuItem
                to={LOGISTICS_ROUTE}
                primaryText={translate("logistics.overview.title")}
                leftIcon={<DashboardOutlined sx={{ color: color.typographyBase }} />}
              />
              <CustomMenuItem
                to={LOGISTICS_INVENTORY_ROUTE}
                primaryText={translate("logistics.inventory.title")}
                leftIcon={<Inventory2Outlined sx={{ color: color.typographyBase }} />}
              />
            </>
          )}
          {can(ResourceName.LogisticsDhlSettings) && (
            <CustomMenuItem
              to={LOGISTICS_DHL_SETTINGS_ROUTE}
              primaryText={translate("dhlSettings.title")}
              leftIcon={<Extension sx={{ color: color.typographyBase }} />}
            />
          )}
          <Divider sx={{ mx: space.xs, my: space.xs }} />
        </Box>
      )}
      {(can(ResourceName.FinanceNotifications) ||
        can(ResourceName.BillwerkCustomerMatch) ||
        can(ResourceName.BillwerkPlanVariants)) && (
        <Box data-testid="finance-menu">
          <SectionTitle>{translate("financeNotifications.section")}</SectionTitle>
          {can(ResourceName.FinanceNotifications) && (
            <CustomMenuItem
              to={FINANCE_NOTIFICATIONS_ROUTE}
              primaryText={translate("financeNotifications.title")}
              leftIcon={<NotificationsOutlined sx={{ color: color.typographyBase }} />}
            />
          )}
          {can(ResourceName.BillwerkCustomerMatch) && (
            <CustomMenuItem
              to={BILLWERK_CUSTOMER_MATCH_ROUTE}
              primaryText={translate("billwerkCustomerMatch.title")}
              leftIcon={<CompareArrowsOutlined sx={{ color: color.typographyBase }} />}
            />
          )}
          {can(ResourceName.BillwerkPlanVariants) && (
            <CustomMenuItem
              to={BILLWERK_PLAN_VARIANTS_ROUTE}
              primaryText={translate("billwerkPlanVariants.title")}
              leftIcon={<SellOutlined sx={{ color: color.typographyBase }} />}
            />
          )}
          <Divider sx={{ mx: space.xs, my: space.xs }} />
        </Box>
      )}
      {can(ResourceName.Analytics) && (
        <Box>
          <Typography
            variant="body2"
            my={space.xxs}
            mx={space.xs}
            sx={{
              color: color.secondary40,
              lineHeight: typography.lineHeight.md,
              letterSpacing: typography.letterSpacing.sm,
            }}
          >
            {translate("analytics.title")}
          </Typography>
          <CustomMenuItem
            to="/analytics/trends"
            primaryText={translate("analytics.trends")}
            leftIcon={<TrendingUp sx={{ color: color.typographyBase }} />}
          />
          <CustomMenuItem
            to="/analytics/features"
            primaryText={translate("analytics.features")}
            leftIcon={<Extension sx={{ color: color.typographyBase }} />}
          />
          <CustomMenuItem
            to="/analytics/distributions"
            primaryText={translate("analytics.distributions")}
            leftIcon={<DevicesOther sx={{ color: color.typographyBase }} />}
          />
          <CustomMenuItem
            to="/analytics/content"
            primaryText={translate("analytics.contentUsage.menu")}
            leftIcon={<VideoLibrary sx={{ color: color.typographyBase }} />}
          />
        </Box>
      )}
      {showManagerNavigation && (
        <Box>
          <Typography
            variant="body2"
            my={space.xxs}
            mx={space.xs}
            sx={{
              color: color.secondary40,
              lineHeight: typography.lineHeight.md,
              letterSpacing: typography.letterSpacing.sm,
            }}
          >
            {translate("menu.gettingStarted")}
          </Typography>
          <CustomMenuItem
            to="/onboarding"
            primaryText={translate("resources.onboarding")}
            leftIcon={<RocketLaunch sx={{ color: color.typographyBase }} />}
          />
        </Box>
      )}
      {menuSections.map(section => {
        if (hasStaffAccess) return null;

        const visibleItems = section.items.filter(
          item => can(item.resource) && (!showManagerNavigation || !managerPrimaryResources.includes(item.resource))
        );

        if (visibleItems.length === 0) return null;

        return (
          <Box key={section.titleKey || `section-${menuSections.indexOf(section)}`}>
            {section.titleKey && (
              <Typography
                variant="body2"
                my={space.xxs}
                mx={space.xs}
                sx={{
                  color: color.secondary40,
                  lineHeight: typography.lineHeight.md,
                  letterSpacing: typography.letterSpacing.sm,
                }}
              >
                {translate(section.titleKey)}
              </Typography>
            )}
            {visibleItems.map(item =>
              item.showCount === false ? (
                <CustomMenuItem
                  key={item.resource}
                  to={item.to}
                  primaryText={translate(`resources.${item.resource}`)}
                  leftIcon={item.icon}
                />
              ) : (
                <MenuItemWithCount key={item.resource} resource={item.resource} to={item.to} icon={item.icon} />
              )
            )}
          </Box>
        );
      })}
      {can(ResourceName.Integrations) && (
        <CustomMenuItem
          to={INTEGRATIONS_ROUTE}
          primaryText={translate("resources.integrations")}
          leftIcon={<Extension sx={{ color: color.typographyBase }} />}
        />
      )}
    </Menu>
  );
};
